/**
 * WebSocket event types for real-time scan updates
 */

import { ParsedWebSocketScanEvent } from './scanned-ticket.types';
import { InventoryTicket } from './ticket-types';

/**
 * WebSocket event type enum
 */
export enum WebSocketEventType {
  TicketScanned = 'ticket.scanned',
  TicketCreated = 'ticket.created',
  TicketCancelled = 'ticket.cancelled',
}

/**
 * Seat information
 */
export interface SeatInfo {
  row: string;
  section: string;
  number: string;
  seat_category: {
    id: number;
    name: string;
    color: string;
    gates: unknown[];
  };
}

/**
 * Event information
 */
export interface EventInfo {
  id: number;
  name: string;
  venue: {
    name: string;
  };
}

/**
 * Scan event data payload
 */
export interface ScanEventData {
  ticket_id: number;
  scan_id: string;
  event_id: number;
  device_id: string;
  status: string;
  holder_name: string;
  seat_label: string;
  entry_count: number;
  max_entries: number;
  timestamp: number;
  stream_id: string;
  scanned_by: {
    user_id: number;
    user_name: string;
    gate_id: number;
    gate_name: string;
  };
  seat: SeatInfo;
  event: EventInfo;
}

/**
 * Base WebSocket message wrapper
 */
interface BaseWebSocketMessage {
  type: WebSocketEventType;
  event_id: string;
  timestamp: number;
}

/**
 * Scan event message - emitted when a ticket is scanned
 */
export interface ScanWebSocketMessage extends BaseWebSocketMessage {
  type: WebSocketEventType.TicketScanned;
  data: ScanEventData;
}

/**
 * Ticket creation event data payload
 */
export interface TicketCreatedEventData {
  ticket_id: number;
  event_id: number;
  status: string;
  holder_name: string;
  seat_label: string;
  seat_category_id: number;
  signature: string;
  timestamp: number;
  seat: SeatInfo;
  event: EventInfo;
}

/**
 * Ticket created message - emitted when a new ticket is created
 */
export interface TicketCreatedWebSocketMessage extends BaseWebSocketMessage {
  type: WebSocketEventType.TicketCreated;
  data: TicketCreatedEventData;
}

/**
 * Ticket cancelled event data payload
 * Extends TicketCreatedEventData with cancelled_at timestamp
 */
export interface TicketCancelledEventData extends TicketCreatedEventData {
  cancelled_at: string;
}

/**
 * Ticket cancelled message - emitted when a ticket is cancelled
 */
export interface TicketCancelledWebSocketMessage extends BaseWebSocketMessage {
  type: WebSocketEventType.TicketCancelled;
  data: TicketCancelledEventData;
}

/**
 * Union type for all WebSocket messages (raw from server)
 */
export type WebSocketMessage =
  | ScanWebSocketMessage
  | TicketCreatedWebSocketMessage
  | TicketCancelledWebSocketMessage;

/**
 * Parsed ticket created event
 */
export interface ParsedTicketCreatedEvent {
  type: WebSocketEventType.TicketCreated;
  data: InventoryTicket;
  event_id: string;
  timestamp: number;
}

/**
 * Parsed ticket cancelled event
 */
export interface ParsedTicketCancelledEvent {
  type: WebSocketEventType.TicketCancelled;
  data: InventoryTicket;
  cancelled_at: number;
  event_id: string;
  timestamp: number;
}

/**
 * Union type for processed WebSocket events
 */
export type WebSocketEvent =
  | ParsedWebSocketScanEvent
  | ParsedTicketCreatedEvent
  | ParsedTicketCancelledEvent;
